import { useState, useCallback, useMemo } from 'react';
import { useAuth } from '@/context/AuthContext';
import {
  storePinHash,
  verifyPin as verifyPinCrypto,
  hasPinSetup as hasPinSetupCrypto,
  clearPin as clearPinCrypto,
  validatePin,
} from '@/lib/core/pin-crypto';

export interface UsePinAuthReturn {
  hasPin: boolean;
  isLoading: boolean;
  setupPin: (pin: string) => Promise<{ success: boolean; error?: string }>;
  verifyPin: (pin: string) => Promise<boolean>;
  clearPin: () => void;
  validatePin: (pin: string) => string | null;
}

export function usePinAuth(): UsePinAuthReturn {
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);

  const userId = user?.id;

  const hasPin = useMemo(() => {
    if (!userId) return false;
    return hasPinSetupCrypto(userId);
  }, [userId]);

  const setupPin = useCallback(async (pin: string): Promise<{ success: boolean; error?: string }> => {
    if (!userId) {
      return { success: false, error: 'Not authenticated' };
    }

    const validationError = validatePin(pin);
    if (validationError) {
      return { success: false, error: validationError };
    }

    setIsLoading(true);
    try {
      await storePinHash(userId, pin);
      return { success: true };
    } catch (error) {
      console.error('Error setting up PIN:', error);
      return { success: false, error: 'Failed to set up PIN' };
    } finally {
      setIsLoading(false);
    }
  }, [userId]);

  const verifyPin = useCallback(async (pin: string): Promise<boolean> => {
    if (!userId) return false;

    setIsLoading(true);
    try {
      return await verifyPinCrypto(userId, pin);
    } catch (error) {
      console.error('Error verifying PIN:', error);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [userId]);

  const clearPin = useCallback(() => {
    if (!userId) return;
    clearPinCrypto(userId);
  }, [userId]);

  return {
    hasPin,
    isLoading,
    setupPin,
    verifyPin,
    clearPin,
    validatePin,
  };
}
